import React from 'react';
import { FormattedMessage } from '@kyruus/intl';
import { Link } from 'react-router-dom';
import { Button } from '@biome-design-system/ui/Button';
import { Box } from '@biome-design-system/ui/Box';

import LoadingOverlay from 'Src/shared/loading';
import { messages } from 'Src/shared/messages';
import { SideSheet } from 'Src/shared/SideSheet';
import {
  getClearFiltersSearch,
  getLogClearSearchFilters
} from '../../utils/search-common';
import { TRACKING_EVENTS } from '../../search-v9/filter-bar/constants';
import { FacetPanel } from '../../search-v9/facet-panel';

/**
 * Renders the side drawer filter panel component.
 *
 * @param {Object} props - The component props.
 * @param {Function} props.onClose - Function to close the side drawer.
 * @param {boolean} props.showMobileFacets - Flag indicating whether to show mobile facets.
 * @param {string} props.searchSummary - The search summary.
 * @param {Array} props.sortOptions - The sort options.
 * @param {Object} props.log - The log object.
 * @param {Function} props.getUpdatedSearch - Function to get updated search.
 * @param {Function} props.toggleMobileFacets - Function to toggle mobile facets.
 * @param {Object} props.facets - The facets object.
 * @param {boolean} props.distanceSort - Flag indicating whether to sort by distance.
 * @param {Object} props.config - The configuration object.
 * @returns {JSX.Element} The rendered SideDrawerFilterPanel component.
 */
export const SideDrawerFilterPanel = ({
  config,
  distanceSort,
  facets,
  getUpdatedSearch,
  loading,
  log,
  onClose,
  searchSummary
}) => {
  const handleCloseDrawer = () => {
    onClose();
    log(TRACKING_EVENTS.ALL_FILTERS_CLOSE_DRAWER);
  };

  const handleClickViewResults = () => {
    onClose();
    log(TRACKING_EVENTS.FILTER_PANEL_VIEW_RESULTS);
  };
  const hasSearchParamsApplied =
    searchSummary.has_filters ||
    searchSummary.filter?.length ||
    searchSummary.location;

  const DrawerHeader = (
    <Box
      id="search-filter-drawer-title"
      sx={(theme) => ({
        fontSize: theme.font_size_heading_5,
        fontWeight: theme.font_weight_bolder
      })}
    >
      <FormattedMessage {...messages.searchFilterDrawerTitle} />
    </Box>
  );
  const DrawerFooter = (
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      {hasSearchParamsApplied && (
        <Link
          to={getClearFiltersSearch(getUpdatedSearch)}
          id="clear-filters"
          className="clear-filters"
          data-testid="clear-filters-button"
          onClick={getLogClearSearchFilters(log)}
          variant="outlined"
          color="neutral"
          component={Button}
        >
          <FormattedMessage {...messages.clearButton} />
        </Link>
      )}
      <Button
        data-testid="searchFilterDrawerViewResults"
        variant="contained"
        onClick={handleClickViewResults}
      >
        <FormattedMessage {...messages.searchFilterDrawerViewResults} />
      </Button>
    </Box>
  );

  return (
    <SideSheet
      ariaLabelledBy="search-filter-drawer-title"
      dataTestId="SearchFilterDrawer"
      footer={DrawerFooter}
      header={DrawerHeader}
      id="search-filter-drawer"
      onClose={handleCloseDrawer}
      scrollIntoViewOnMobile={true}
      width={{ xs: '100vw', md: '550px' }}
    >
      <LoadingOverlay loading={loading} />
      <FacetPanel
        config={config}
        distanceSort={distanceSort}
        facets={facets}
        getUpdatedSearch={getUpdatedSearch}
        log={log}
        searchSummary={searchSummary}
        showLocationFacet={true}
      />
    </SideSheet>
  );
};
